import { ChevronsUpDown, LogOut } from "lucide-react";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { sair } from "@/lib/auth-client";
import { useBadges } from "@/lib/hooks/useBadges";
import { useMe } from "@/lib/hooks/useMe";
import { invalidateMe } from "@/lib/me";
import { PERSONA_LABEL } from "@/lib/persona";
import { Link, usePathname, useRouter } from "@/lib/nav";
import { cn } from "@/lib/utils";

import { itemAtivo, NAV_FOOTER, NAV_SECTIONS, podeVer, secoesVisiveis, type NavItemDef } from "./nav-sections";

import type { Badges } from "@alianca/core/contracts/identidade";

const TODOS_OS_ITENS = [...NAV_SECTIONS.flatMap((s) => s.items), ...NAV_FOOTER];

/** Sidebar flutuante clara (docs/DESIGN-SYSTEM.md › Layout): sem botões de ação nem seletor de produto. */
export function AppSidebar() {
    const pathname = usePathname();
    const { persona } = useMe();
    const badges = useBadges();
    const ativo = itemAtivo(pathname, TODOS_OS_ITENS);
    const rodape = NAV_FOOTER.filter((i) => podeVer(i, persona));

    return (
        <aside className="m-3 flex w-sidebar shrink-0 flex-col rounded-2xl bg-surface px-3 pb-3 pt-4 shadow-float">
            <div className="flex justify-center px-2 pb-4 pt-1">
                <img src="/assets/logo-alianca-app.png" alt="Aliança Renovável" className="h-12 w-auto" />
            </div>

            <nav aria-label="Principal" className="scrollbar-discreta -mx-1 flex-1 overflow-y-auto px-1">
                {secoesVisiveis(persona).map((secao, i) => (
                    <div key={secao.title ?? i} className="flex flex-col gap-0.5">
                        {secao.title && <p className="px-2.5 pb-1.5 pt-3.5 text-group-label text-label-tertiary">{secao.title}</p>}
                        {secao.items.map((item) => (
                            <NavItem key={item.href} item={item} active={item === ativo} badges={badges} />
                        ))}
                    </div>
                ))}
            </nav>

            <div className="mt-2 flex flex-col gap-1 border-t border-separator pt-2.5">
                {rodape.map((item) => (
                    <NavItem key={item.href} item={item} active={item === ativo} badges={badges} />
                ))}
                <UserMenu />
            </div>
        </aside>
    );
}

function NavItem({ item, active, badges }: { item: NavItemDef; active: boolean; badges: Badges | null }) {
    const badge = badges && item.badge ? item.badge(badges) : 0;
    const count = badges && item.count ? item.count(badges) : 0;
    const Icon = item.icon;
    return (
        <Link
            href={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
                "flex h-nav-item items-center gap-[11px] rounded-md px-2.5 text-body font-medium transition-colors duration-150",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green",
                active ? "bg-green-gradient font-semibold text-on-green shadow-green" : "text-label hover:bg-fill",
            )}
        >
            <Icon className={cn("size-[18px] shrink-0", active ? "text-on-green" : "text-label-secondary")} strokeWidth={1.9} aria-hidden />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {badge > 0 && (
                <span className="h-5 min-w-5 rounded-full bg-badge-red px-[5px] text-center text-[12px] font-semibold leading-5 text-on-green">
                    {badge}
                </span>
            )}
            {badge === 0 && count > 0 && (
                <span className={cn("text-[12px]", active ? "text-on-green" : "text-label-tertiary")}>{count.toLocaleString("pt-BR")}</span>
            )}
        </Link>
    );
}

function UserMenu() {
    const me = useMe();
    const router = useRouter();
    const nome = me.nome ?? me.email;
    const iniciais = nome
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]!.toUpperCase())
        .join("");

    const handleLogout = async () => {
        await sair().catch(() => undefined);
        invalidateMe();
        router.push("/login");
        router.refresh();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2.5 rounded-md bg-fill p-2 text-left text-label-tertiary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green">
                <span className="grid size-[34px] shrink-0 place-items-center rounded-full bg-green-gradient text-footnote font-semibold text-on-green">
                    {iniciais}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                    <b className="truncate text-subhead font-semibold text-label">{nome}</b>
                    <span className="truncate text-[12px] leading-4 text-label-secondary">{PERSONA_LABEL[me.persona]}</span>
                </span>
                <ChevronsUpDown className="size-4 shrink-0" aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                <DropdownMenuItem onSelect={handleLogout}>
                    <LogOut aria-hidden />
                    Sair
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
